import { useEffect, useState } from 'react'
import { useAuth } from '@/features/auth/AuthProvider'
import { removeCrmUser, isCrmDomainEmail, loadLinkTargets,
  saveCrmUser, subscribeToCrmUsers, CRM_EMAIL_DOMAIN } from '@/features/auth/repository/crmUserRepository'
import { ShareholderAccess } from './ShareholderAccess'
import { isStaffRole, ROLE_LABELS, type CrmRole, type CrmUser } from '@/types/crm'
import { formatDate } from '@/shared/util/dates'

const EMPTY: CrmUser = { email: '', displayName: '', title: '', roles: ['sales'], disabled: false }

/** One line saying what each outside role can reach. Shown where it is chosen. */
const SCOPE_NOTE: Record<CrmRole, string> = {
  admin: '',
  sales: '',
  externalSales:
    'Sees only the leads assigned to this address. No events, no partners, no ambassadors, and nothing from the share register.',
  shareholder:
    'Sees their own holding and the cap table — holder name, shares and percentage. Not the register itself: no personnummer, no addresses, no contact details, no internal notes.',
  ambassador:
    'Sees and maintains their own ambassador record — how to reach them and what they ride — and their own upload folder. Nothing about the programme or anybody else in it.',
}

/**
 * Who has access, and how they sign their emails.
 *
 * Two jobs in one screen, because they are the same record. The access list was
 * previously managed in the Firestore console; the name and title are new, and
 * they are what the outreach templates merge into `[Avsender]` and `[Tittel]` —
 * so a rep with no record here sends email signed by a placeholder.
 *
 * Note what the list cannot show: anyone on the @{CRM_EMAIL_DOMAIN} domain is
 * let in by the Firestore rules without a document at all. They will not appear
 * until somebody adds them, which is exactly the reason to add them.
 */
export function TeamEditor({ readOnly }: { readOnly: boolean }) {
  const [users, setUsers] = useState<CrmUser[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<CrmUser | null>(null)
  const [saving, setSaving] = useState(false)
  /** Holds the email awaiting confirmation, so removal takes two deliberate clicks. */
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null)

  /**
   * The records an outside login can be linked to.
   *
   * Read only while this screen is open, and only by an admin — the rules allow
   * the register to be listed by nobody else. Names only; nothing else from
   * either record reaches this component.
   */
  const [shareholders, setShareholders] = useState<{ id: string; name: string }[]>([])
  const [ambassadors, setAmbassadors] = useState<{ id: string; name: string }[]>([])
  useEffect(() => {
    let live = true
    void loadLinkTargets()
      .then((t) => {
        if (!live) return
        setShareholders(t.shareholders)
        setAmbassadors(t.ambassadors)
      })
      .catch(() => {
        if (!live) return
        setShareholders([])
        setAmbassadors([])
      })
    return () => {
      live = false
    }
  }, [])
  const { user } = useAuth()
  /**
   * Nobody removes their own access.
   *
   * Access is granted by the `skidaCrmUsers` record existing, so deleting your
   * own is a one-way door: the app would refuse you at the next sign-in and the
   * only way back is a script with admin credentials. It is blocked here rather
   * than warned about, because a confirmation dialog is no protection against a
   * misclick on the row you happen to be looking at — your own.
   */
  const me = (user?.email ?? '').trim().toLowerCase()

  // Only admins may list `skidaCrmUsers` — the rules let everyone read their own
  // record and nothing else. Subscribing as a sales rep would produce a
  // permission error rather than a list, so it does not subscribe at all.
  useEffect(() => {
    if (readOnly) {
      setUsers([])
      return
    }
    return subscribeToCrmUsers(
      (list) => setUsers(list),
      (err) => setError(err.message),
    )
  }, [readOnly])

  const submit = async () => {
    if (!draft || !draft.email.trim() || saving) return
    setSaving(true)
    setError(null)
    try {
      await saveCrmUser(draft)
      setDraft(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  /**
   * The only administrator left, if there is exactly one.
   *
   * Derived from the list already on screen rather than re-queried: the domain
   * route never grants admin, so an explicit record is the only way to be one,
   * and a revoked record grants nothing.
   */
  const lastAdmin = (() => {
    const admins = (users ?? []).filter((u) => u.roles.includes('admin') && !u.disabled)
    return admins.length === 1 ? admins[0]!.email.trim().toLowerCase() : null
  })()

  /**
   * Who belongs where — 3 Sep 2026.
   *
   * Three groups, because one column had been answering three questions.
   * **Team** is decided by the address: anybody on @{CRM_EMAIL_DOMAIN} is one of
   * us whatever else they are, and a colleague who happens to own shares is
   * still a colleague. **External** is everybody else who does a job here — an
   * outside salesperson, an ambassador, an off-domain admin.
   *
   * A holder and nothing more falls through both, deliberately: they are the
   * register, and the register is a table of its own below. `ShareholderAccess`
   * lists every one of them whether or not they have an account, and picks up
   * any shareholder login this pair would otherwise have dropped.
   */
  const staff = (users ?? []).filter((u) => isCrmDomainEmail(u.email))
  const external = (users ?? []).filter(
    (u) => !isCrmDomainEmail(u.email) && u.roles.some((r) => r !== 'shareholder'),
  )

  const row = (u: CrmUser) => (
    <div className={`team__row${u.disabled ? ' team__row--off' : ''}`} key={u.email}>
      <div className="team__name">
        {u.displayName}
        {u.disabled && <span className="team__off">access revoked</span>}
      </div>
      <div className="muted">{u.title || <span className="team__gap">no title set</span>}</div>
      <div className="muted team__email">{u.email}</div>
      <div className="muted">
        {u.lastSignInAt ? formatDate(new Date(u.lastSignInAt).toISOString()) : '—'}
      </div>
      <div>
        {/* Every role, not the first — the row has to say what the person
            actually is, or the list stops being the answer to "who can
            see what". */}
        {u.roles.map((r) => (
          <span className={`pill team__role team__role--${r}`} key={r}>
            {ROLE_LABELS[r]}
          </span>
        ))}
      </div>
      <div className="team__actions">
        <button className="btn-mini" disabled={readOnly} onClick={() => setDraft({ ...u })}>
          Edit
        </button>
        {u.email.trim().toLowerCase() === me ? (
          <button
            className="btn-mini"
            disabled
            title="You cannot remove your own access — you would be locked out with no way back from inside the app."
          >
            Remove
          </button>
        ) : u.email.trim().toLowerCase() === lastAdmin ? (
          <button
            className="btn-mini"
            disabled
            title="This is the last administrator. Only an admin can grant admin, so removing them would leave the CRM with no way back except the Firestore console. Make somebody else an admin first."
          >
            Remove
          </button>
        ) : confirmRemove === u.email ? (
          <>
            <button
              className="btn-mini team__danger"
              disabled={saving}
              onClick={async () => {
                setSaving(true)
                setError(null)
                try {
                  await removeCrmUser(u.email, me)
                  setConfirmRemove(null)
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Could not remove.')
                } finally {
                  setSaving(false)
                }
              }}
            >
              Confirm
            </button>
            <button className="btn-mini" onClick={() => setConfirmRemove(null)}>
              Cancel
            </button>
          </>
        ) : (
          <button
            className="btn-mini"
            disabled={readOnly}
            onClick={() => setConfirmRemove(u.email)}
            title={
              'Removes their CRM access. Access is granted by this record existing, so ' +
              'deleting it is what revokes it.'
            }
          >
            Remove
          </button>
        )}
      </div>
    </div>
  )

  const group = (title: string, list: CrmUser[], empty: string) => (
    <div className="team__group">
      <div className="team__grouphead">{title}</div>
      <div className="team__head">
        <div>Name</div>
        <div>Title</div>
        <div>Email</div>
        <div>Last sign-in</div>
        <div>Role</div>
        <div />
      </div>
      {list.map(row)}
      {list.length === 0 && <div className="empty-note">{empty}</div>}
    </div>
  )

  return (
    <>
      <div className="admincard">
        <div className="card-label">Team &amp; access</div>

        {error && <div className="admin__error">{error}</div>}

        {readOnly && (
          <div className="empty-note">
            Only an admin can see or change the team list. Ask one to set your name and title —
            without them, the outreach templates sign your emails with a placeholder.
          </div>
        )}

        {!readOnly && users === null && <div className="empty-note">Loading…</div>}

        {!readOnly && users !== null && (
          <>
            {group(
              'Team',
              staff,
              `Nobody on @${CRM_EMAIL_DOMAIN} is listed yet — and without a record here, nobody can sign in.`,
            )}
            {group('External', external, 'Nobody outside the company holds a role beyond shareholder.')}
          </>
        )}

        {!readOnly && (
          <button className="addstage team__add" onClick={() => setDraft({ ...EMPTY })}>
            + Add person
          </button>
        )}

        {draft && (
          <div className="team__form">
            <div className="card-label">
              {users?.some((u) => u.email === draft.email) ? 'Edit person' : 'Add person'}
            </div>

            <div className="team__formgrid">
              <label className="field">
                <span>Name</span>
                <input
                  className="input"
                  value={draft.displayName}
                  onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                  placeholder="Trond Marius Gabrielsen"
                  autoFocus
                />
              </label>
              <label className="field">
                <span>Title</span>
                <input
                  className="input"
                  value={draft.title ?? ''}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="Chief Commercial Officer, Mountainsnow AS / Skida"
                />
              </label>
              <label className="field">
                <span>Email</span>
                <input
                  className="input"
                  value={draft.email}
                  onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                  placeholder={`name@${CRM_EMAIL_DOMAIN}`}
                  // The address is the document id, so changing it on an existing
                  // person would create a second record rather than rename one.
                  disabled={users?.some((u) => u.email === draft.email)}
                />
              </label>
              <fieldset className="field team__roles">
                <legend>Roles</legend>
                {/*
                  Checkboxes, because a person can be two things — 29 Aug 2026.
                  Access is the union of what is ticked, so ticking more can only
                  ever add. The last administrator's admin box is locked for the
                  same reason the select was: only an admin can make an admin, so
                  unticking the last one leaves nobody who can undo it.
                */}
                {(Object.keys(ROLE_LABELS) as CrmRole[]).map((r) => {
                  const on = draft.roles.includes(r)
                  const lockAdmin = r === 'admin' && draft.email.trim().toLowerCase() === lastAdmin
                  return (
                    <label className="team__rolebox" key={r}>
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={lockAdmin}
                        title={
                          lockAdmin
                            ? 'This is the last administrator. Make somebody else an admin first.'
                            : undefined
                        }
                        onChange={(e) => {
                          const roles = e.target.checked
                            ? [...draft.roles, r]
                            : draft.roles.filter((x) => x !== r)
                          setDraft({
                            ...draft,
                            // Never empty: a row with no role is somebody on the
                            // allowlist who can sign in and see nothing, which
                            // reads as a broken account rather than a decision.
                            roles: roles.length ? roles : ['sales'],
                            // Drop a link the roles no longer use, so a stale
                            // pointer cannot grant access if the role comes back.
                            shareholderId: roles.includes('shareholder') ? draft.shareholderId : '',
                            ambassadorId: roles.includes('ambassador') ? draft.ambassadorId : '',
                          })
                        }}
                      />
                      {ROLE_LABELS[r]}
                    </label>
                  )
                })}
              </fieldset>
            </div>

            {/*
              What an outside role is allowed to see, and of what — 29 Aug 2026.

              The three roles below are people who are not staff, and each sees
              exactly one thing. Two of them need to be told *which* one, and it is
              an explicit link rather than a match on the email field of the
              record: anybody who could get an address onto a shareholder record
              could otherwise read it.
            */}
            {draft.roles.some((r) => !isStaffRole(r)) && (
              <div className="team__scope">
                {draft.roles
                  .filter((r) => !isStaffRole(r))
                  .map((r) => (
                    <p className="recform__note" key={r}>
                      <strong>{ROLE_LABELS[r]}.</strong> {SCOPE_NOTE[r]}
                    </p>
                  ))}

                {draft.roles.includes('shareholder') && (
                  <label className="field">
                    <span>Which shareholder</span>
                    <select
                      value={draft.shareholderId ?? ''}
                      onChange={(e) => setDraft({ ...draft, shareholderId: e.target.value })}
                    >
                      <option value="">Not linked — they will see nothing</option>
                      {shareholders.map((h) => (
                        <option key={h.id} value={h.id}>
                          {h.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {draft.roles.includes('ambassador') && (
                  <label className="field">
                    <span>Which ambassador</span>
                    <select
                      value={draft.ambassadorId ?? ''}
                      onChange={(e) => setDraft({ ...draft, ambassadorId: e.target.value })}
                    >
                      <option value="">Not linked — they will see nothing</option>
                      {ambassadors.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {draft.roles.includes('externalSales') && (
                  <p className="recform__note">
                    They see the leads whose owner is this address, and nothing else — set the owner
                    on each lead from the pipeline.
                  </p>
                )}
              </div>
            )}

            <label className="modal__check team__revoke">
              <input
                type="checkbox"
                checked={draft.disabled === true}
                // Same reason as the role: a revoked admin is not an admin, so
                // revoking the last one leaves nobody who can undo it.
                disabled={draft.email.trim().toLowerCase() === lastAdmin}
                onChange={(e) => setDraft({ ...draft, disabled: e.target.checked })}
              />
              Revoke access — keeps the record, blocks sign-in
              {draft.email.trim().toLowerCase() === lastAdmin && (
                <span className="team__off">last admin — cannot be revoked</span>
              )}
            </label>

            <div className="admincard__actions">
              <button className="btn btn--ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </button>
              <button
                className="btn btn--primary"
                onClick={() => void submit()}
                disabled={!draft.email.trim() || !draft.displayName.trim() || saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}

        <div className="micro admincard__note">
          Name and title are merged into every outreach template as{' '}
          <code>[Avsender]</code> and <code>[Tittel]</code>, and travel with the mailto link into the
          mail client. A person with no title here simply gets a signature without one — the line is
          dropped rather than left blank.
          <br />
          Access itself is enforced by the Firestore rules, not by this screen. Anyone with a verified
          @{CRM_EMAIL_DOMAIN} address can sign in as <code>sales</code> whether or not they are listed;
          a record is required only to grant <code>admin</code>, to let in an outside address, or to
          revoke either.
          <br />
          Last sign-in comes from Firebase Auth, which is the only thing that knows — and only the
          Admin SDK can read it. <code>scripts/syncStaffUsers.ts</code> copies it across and adds a
          record for anyone on the domain who has signed in but was never listed here. Re-run it after
          somebody new joins.
        </div>
      </div>

      {/* The register, below the people who work here. Admin only, like the list
          above — the rules let nobody else list either collection. */}
      {!readOnly && users !== null && <ShareholderAccess users={users} />}
    </>
  )
}
